import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { 
  Download, 
  FileText, 
  Palette, 
  BookOpen, 
  Sticker, 
  ImageIcon,
  Loader2,
  ExternalLink
} from "lucide-react";

interface StoryEnhancementsProps {
  storyId: number;
  storyTitle: string;
  storyContent: string;
}

interface Illustration {
  sceneIndex: number;
  imageUrl: string;
  prompt: string;
}

interface CharacterSticker {
  character: string;
  stickerUrl: string;
  index: number;
}

export function StoryEnhancements({ storyId, storyTitle, storyContent }: StoryEnhancementsProps) {
  const { toast } = useToast();
  const [illustrations, setIllustrations] = useState<Illustration[]>([]);
  const [stickers, setStickers] = useState<CharacterSticker[]>([]);
  const [progressMessage, setProgressMessage] = useState<string>("");

  const generateIllustrationsMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/stories/${storyId}/illustrations`);
      return await res.json();
    },
    onSuccess: (data) => {
      setIllustrations(data.illustrations);
      toast({
        title: "Illustrations generated!",
        description: `Created ${data.illustrations.length} beautiful illustrations for your story.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to generate illustrations",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const generateStickersMutation = useMutation({
    mutationFn: async () => {
      if (!storyContent?.trim()) {
        throw new Error('Story content is required to generate stickers');
      }

      if (!storyId) {
        throw new Error('Story ID is required to generate stickers');
      }

      setStickers([]);
      setProgressMessage('Analyzing story characters...');

      try {
        const response = await apiRequest('POST', `/api/stories/${storyId}/stickers-chunked`);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Network error occurred' }));
          throw new Error(errorData.message || `Server error: ${response.status}`);
        }

        return response.body!;
      } catch (error) {
        console.error('Sticker generation API error:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      setStickers(data.stickers);
      toast({
        title: "Character stickers created!",
        description: `Generated ${data.stickers.length} fun stickers of your story characters.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to generate stickers",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const downloadStoryPDF = () => {
    // Create a simple PDF download using HTML content
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${storyTitle}</title>
            <style>
              body { font-family: 'Inter', sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; line-height: 1.6; }
              h1 { color: #6B46C1; text-align: center; margin-bottom: 30px; }
              p { margin-bottom: 15px; }
              .page-break { page-break-before: always; }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            <h1>${storyTitle}</h1>
            ${storyContent.split('\n\n').map(paragraph => `<p>${paragraph}</p>`).join('')}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const downloadColoringBook = () => {
    // Create a coloring book PDF with illustrations
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const illustrationElements = illustrations.map(ill => 
        `<div style="page-break-before: always; text-align: center;">
          <h2 style="color: #6B46C1; margin-bottom: 20px;">Color this scene!</h2>
          <img src="${ill.imageUrl}" style="max-width: 100%; height: 400px; object-fit: contain; border: 2px solid #ccc; filter: grayscale(100%) contrast(150%);" />
          <p style="margin-top: 20px; font-style: italic;">${ill.prompt}</p>
        </div>`
      ).join('');

      printWindow.document.write(`
        <html>
          <head>
            <title>${storyTitle} - Coloring Book</title>
            <style>
              body { font-family: 'Inter', sans-serif; margin: 20px; }
              h1 { color: #6B46C1; text-align: center; margin-bottom: 30px; }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            <h1>${storyTitle} Coloring Book</h1>
            ${illustrationElements}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const orderPhysicalBook = () => {
    // Redirect to a print-on-demand service
    const bookUrl = `https://www.blurb.com/b/create?title=${encodeURIComponent(storyTitle)}`;
    window.open(bookUrl, '_blank');

    toast({
      title: "Opening print service",
      description: "We'll help you create a professional printed book!",
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
                            <img src="/logo.png" alt="SnuggleCrafters Logo" className="h-5 w-5 object-contain" />
            Enhance Your Story
          </CardTitle>
          <CardDescription>
            Transform your story with illustrations, downloads, and more!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Generate Illustrations */}
            <div className="space-y-3">
              <Button
                onClick={() => generateIllustrationsMutation.mutate()}
                disabled={generateIllustrationsMutation.isPending}
                className="w-full"
                variant="gradient"
              >
                {generateIllustrationsMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ImageIcon className="h-4 w-4 mr-2" />
                )}
                Generate Illustrations
              </Button>
              {illustrations.length > 0 && (
                <Badge variant="secondary" className="w-full justify-center">
                  {illustrations.length} illustrations created
                </Badge>
              )}
            </div>

            {/* Download Story PDF */}
            <Button
              onClick={downloadStoryPDF}
              variant="gradient"
              className="w-full text-white"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Story PDF
            </Button>

            {/* Download Coloring Book */}
            <Button
              onClick={downloadColoringBook}
              disabled={illustrations.length === 0}
              variant="gradient"
              className="w-full text-white"
            >
              <Palette className="h-4 w-4 mr-2" />
              Download Coloring Book
              {illustrations.length === 0 && (
                <span className="text-xs ml-1">(Generate illustrations first)</span>
              )}
            </Button>

            {/* Order Physical Book */}
            <Button
              onClick={orderPhysicalBook}
              variant="gradient"
              className="w-full text-white"
            >
              <BookOpen className="h-4 w-4 mr-2" />
              Order Physical Book
              <ExternalLink className="h-3 w-3 ml-1" />
            </Button>

            {/* Generate Character Stickers */}
            <div className="space-y-3 md:col-span-2">
              <Button
                onClick={() => generateStickersMutation.mutate()}
                disabled={generateStickersMutation.isPending}
                className="w-full"
                variant="outline"
              >
                {generateStickersMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sticker className="h-4 w-4 mr-2" />
                )}
                Create Fun Character Stickers
              </Button>
              {stickers.length > 0 && (
                <Badge variant="secondary" className="w-full justify-center">
                  {stickers.length} character stickers created
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Display Generated Illustrations */}
      {illustrations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-purple-600" />
              Story Illustrations
            </CardTitle>
            <CardDescription>
              Beautiful AI-generated illustrations for your story
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {illustrations.map((illustration, index) => (
                <div key={index} className="space-y-2">
                  <img
                    src={illustration.imageUrl}
                    alt={`Illustration ${index + 1}`}
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                  <p className="text-sm text-gray-600">{illustration.prompt}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Display Generated Stickers */}
      {stickers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sticker className="h-5 w-5 text-purple-600" />
              Character Stickers
            </CardTitle>
            <CardDescription>
              Fun stickers of your story characters
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
              {stickers.map((sticker, index) => (
                <div key={index} className="text-center space-y-2">
                  <img
                    src={sticker.stickerUrl}
                    alt={`${sticker.character} sticker`}
                    className="w-full h-32 object-cover rounded-lg border"
                  />
                  <p className="text-sm font-medium">{sticker.character}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}